/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useRef, useState } from 'react'
import type { CustomCellEditorProps } from 'ag-grid-react'
import colors from '@sportninja/common/constants/appColors'
import ModernPhoneInput from 'src/components/ts-components/ModernPhoneInput'

/**
 * Inline phone editor for the roster grids.
 *
 * Wraps the SAME `ModernPhoneInput` the add/edit modals use, so the country
 * selector, flag and formatting behave identically in the grid — a plain text
 * cell would have let someone type a bare local number with no country.
 *
 * Runs as a POPUP editor (`cellEditorPopup` on the column): the control is
 * 40px tall with a country dropdown, and an in-cell editor would be clipped by
 * the row height.
 *
 * The value is E.164 (`+15551234567`), which already encodes the country — so
 * changing either the country or the digits produces a new value through the
 * one `onValueChange`, and no separate country field has to be threaded here.
 */
const PhoneCellEditor = ({
  value,
  onValueChange,
  stopEditing,
}: CustomCellEditorProps<unknown, string>) => {
  'use memo'

  // Held locally and pushed up on every keystroke: AG Grid reads its own
  // `value` back when editing stops, so the two must not drift.
  const [draft, setDraft] = useState<string>(value ?? '')
  const containerRef = useRef<HTMLDivElement>(null)

  const handleChange = (next: string) => {
    // `react-phone-number-input` yields undefined for an empty/partial number;
    // '' keeps the value a string so clearing the cell reads as a real edit.
    const normalised = next ?? ''
    setDraft(normalised)
    onValueChange(normalised)
  }

  // Commit on click-away. The popup sits outside the cell, so AG Grid's own
  // blur handling does not see it — without this the editor would stay open
  // after clicking elsewhere in the grid.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        stopEditing()
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [stopEditing])

  return (
    <div
      ref={containerRef}
      css={wrapperStyle}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          stopEditing()
        } else if (event.key === 'Escape') {
          // Cancel: hand back the value editing started with.
          onValueChange(value ?? '')
          stopEditing(true)
        }
      }}
    >
      <ModernPhoneInput
        value={draft}
        onChange={handleChange}
        label=''
        hideErrorLabel
      />
    </div>
  )
}

const wrapperStyle = css`
  width: 260px;
  padding: 6px;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.PRIMARY};
  border-radius: 6px;

  /* The shared input reserves space for a label this cell does not show. */
  & > div {
    gap: 0;
  }
`

export default PhoneCellEditor
